<template>
  <tm-popup
    v-model="popupVisible"
    position="bottom"
    :close-on-click-overlay="false"
    class="comment-replies-popup"
    style="height: calc(100% - 48px)"
    @click-overlay="onBeforeCloseReplyDialog"
  >
    <div
      ref="dialogContentRef"
      class="comment-replies-popup__body flex flex-col h-full"
    >
      <div class="popup-title p-4 shadow-sm flex justify-between">
        <div>评论回复</div>
        <tm-icon
          name="cross"
          class="text-base teld-color-gray"
          @click="onBeforeCloseReplyDialog"
        />
      </div>

      <div ref="scrollAreaRef" class="popup-body-scrollable overflow-y-auto flex-1 pb-5">
        <!-- 评论详情 -->
        <div class="section-comment flex px-5 pt-3">
          <!-- 左侧头像 -->
          <div class="left-avatar mr-2" style="">
            <UserAvatar :image-url="dataItem.UserAvatar" />
          </div>

          <div class="flex-1 flex flex-col">
            <!-- 评论顶部信息 -->
            <div class="flex justify-between teld-color-gray mb-3">
              <span class="teld-color-gray font-semibold">{{ userNameDisplay }}</span>
              <!-- <span :title="comment.CommentTime">{{ comment.CommentTimeRelative }}</span> -->
            </div>

            <!-- 评论内容行 -->
            <div class="comment-content mb-3">
              <div
                v-if="!comment.IsDeleted"
                v-html="decodeURIComponent(comment.Content)"
                @click="onClickContent"
              ></div>
              <div v-else class="teld-color-gray">该条评论已删除</div>
            </div>

            <!-- 评论底部行 -->
            <div class="flex justify-between">
              <span :title="comment.CommentTime" class="teld-color-gray">{{
                comment.CommentTimeRelative
              }}</span>
              <div class="comment-operations flex justify-end mb-3"
                :class="[disabled ? 'teld-color-lightgray' : 'teld-theme-color']"
              >
                <!-- 点赞 -->
                <div
                  :tabindex="disabled ? -1 : 0"
                  class="comment-like flex cursor-pointer outline-on-focus leading-5"
                  :class="[disabled ? 'cursor-not-allowed' : '']"
                  @click="onClickLike"
                >
                  <SvgHeartFilled v-show="comment.Liked" class="mr-1" />
                  <SvgHeart v-show="!comment.Liked" class="mr-1" />
                  <span class="">{{ comment.LikeCount || 0 }}</span>
                </div>
              </div>
            </div>

            <!-- 附件 -->
            <section
              v-if="!comment.IsDeleted && comment.Attachments && comment.Attachments.length"
              class="attachments-section mb-2 border border-solid border-gray-100 rounded-sm"
            >
              <div
                v-for="attachment in comment.Attachments"
                :key="attachment.ID"
                class="
                  attachment-file p-2 flex items-center
                "
                @click="handleDownloadFile(attachment)"
              >
                <!-- 文件类型图标 -->
                <FileTypeIcon :file-name="attachment.FileName" class="w-6 h-6 mr-2" />
                <!-- 文件名 -->
                <div
                  class="flex-1 mr-2 van-multi-ellipsis--l2 overflow-hidden"
                  :title="attachment.FileName"
                >
                  {{ attachment.FileName }}
                </div>
              </div>
            </section>
          </div>
        </div>

        <div class="gap h-5 w-full teld-bg-color-gray"></div>

        <!-- 回复列表 -->
        <div class="section-reply-list">
          <div class="reply-count py-3 px-5 border-b border-solid border-gray-100">
            <span class="bg-white w-full">{{ totalReplyCount }} 条回复</span>
          </div>
          <ReplyItem
            v-for="reply in replyList"
            :key="reply.ID"
            :data-item="reply"
            :is-comment-deleted="comment.IsDeleted"
            :total.sync="total"
            class="reply-item-in-comment-detail px-5 pt-3"
            @reply="onReplyToReply"
          />
        </div>
      </div>

      <!-- 输入&发布回复 -->
      <draft-editor
        ref="draftEditorRef"
        :visible="!isLoading && visible"
        :input-indicator="true"
        :placeholder="inputPlaceholder"
        :autoClearOnHidding="true"
        @comment="onReplyToComment"
      />
      
      <PersonnelInfoPopup
        :visible.sync="personnelInfoVisible"
        :user-id.sync="mentionedUserId"
      />
    </div>
  </tm-popup>
</template>

<script>
// Components
import SvgHeart from "../../svg-icons/heart.vue";
import SvgHeartFilled from "../../svg-icons/heart-filled.vue";
import SvgStar from "../../svg-icons/star.vue";
import SvgStarFilled from "../../svg-icons/star-filled.vue";
import ReplyItem from '../reply-item/index.vue';
import DraftEditor from '../draft-editor/index.vue';
import UserAvatar from '../../user-avatar.vue';
import FileTypeIcon from '../../file-type-icon/index.vue';
import PersonnelInfoPopup from '../personnel-info-popup/index.vue';

// Utils
import { scrollPaging, saveAs, checkDirty } from '../../../core';

/**
 * [移动端]评论详情，回复列表 Popup
 */
export default {
  name: 'CommentRepliesPopup',
  inject: ['loginUserInfo', 'pageUrl', 'notificationLink'],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * Comment数据条目
     */
    dataItem: {
      type: Object,
      default: () => {}
    },
  },
  components: {
    SvgHeart,
    SvgHeartFilled,
    SvgStar,
    SvgStarFilled,
    ReplyItem,
    DraftEditor,
    UserAvatar,
    FileTypeIcon,
    PersonnelInfoPopup,
  },
  data() {
    return {
      disabled: false, // TODO 是否在评论详情中增加删除功能？
      comment: {},
      replyList: [],
      isLoading: false,
      hasLoadedAll: false,
      pageIndex: 1,
      pageSize: 4,
      totalReplyCount: 0,
      //
      personnelInfoVisible: false,
      mentionedUserId: null,
    }
  },
  watch: {
    visible(val) {
      if (val) {
        // dialog挂载时插槽内容没有加载，所以mountd时点不行
        setTimeout(() => {
          // 用延时确保插槽内容已挂载，再启动滚动监听
          const scrollArea = this.$refs.scrollAreaRef;
          if (scrollArea) {
            scrollArea.addEventListener('wheel', this.onScroll, true);
          }
          this.init();
        }, 300);
      } else {
        // 关闭后取消滚动监听
        const scrollArea = this.$refs.scrollAreaRef;
        scrollArea && scrollArea.removeEventListener('wheel', this.onScroll);
        this.reset();
      }
    },
  },
  computed: {
    popupVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit('update:visible', v);
      },
    },
    isAuthor() {
      const loginUser = this.loginUserInfo();
      return loginUser.userId === this.comment.UserID;
    },
    userNameDisplay() {
      return this.isAuthor ? '我' : this.comment.UserName;
    },
    inputPlaceholder() {
      return `回复 ${this.userNameDisplay}`;
    },
    total: {
      get() {
        return this.totalReplyCount;
      },
      set(val) {
        this.totalReplyCount = this.comment.ReplyCount = val;
        this.$emit('update:data-item', this.comment);
      }
    },
  },
  methods: {
    async fetchPageData() {
      this.isLoading = true;
      try {
        const payload = {
          CommentId: this.comment.ID,
          TargetID: "",
          PageIndex: this.pageIndex,
          PageCount: this.pageSize,
          Sort: 1, // 回复排序为正序
        }
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl('CMTS-GetRepliesList'),
          data: { input: JSON.stringify(payload)}
        }) || {};

        if (!data || data.state !== '1' || !data.data) {
          // 获取数据失败
          return;
        }

        // 成功获取数据后
        const { Items, TotalCount } = data.data;
        this.replyList = this.replyList.concat(Items);
        this.total = TotalCount;
        this.pageIndex += 1;
        this.hasLoadedAll = this.replyList.length >= TotalCount;
      } catch (error) {
        console.log(error);
      } finally {
        setTimeout(() => {
          this.isLoading = false;
        }, 500);
      }
    },
    init() {
      // Mock for UI development
      // this.replyList = this.dataItem.Replies;
      // setTimeout(() => {
      //   this.isLoading = false;
      // }, 500);

      this.comment = { ...this.dataItem };
      // 获取评论的回复列表
      this.fetchPageData();
    },
    reset() {
      this.replyList = [];
      this.totalReplyCount = 0;
      this.pageIndex = 1;
    },
    async onClickLike() {
      if(this.disabled) return;
      const isLike = this.comment.Liked = !this.comment.Liked;
      const diff = this.comment.Liked ? 1 : -1;
      this.comment.LikeCount += diff;
      let succeeded = false;

      const payload = isLike ? { CommentID: this.comment.ID, TargetID: 0 } : { Id: this.comment.ID };
      const serviceCode = isLike ? 'CMTS-Like' : 'CMTS-CancelLike';
      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl(serviceCode),
          data: isLike ? { comment: JSON.stringify(payload) } : { input: JSON.stringify(payload) },
        });

        if (data.state !== '1') {
          return;
        }

        succeeded = true;
        this.$TM.Toast(isLike ? '已点赞' : '已取消点赞');
      } catch (error) {
        console.log(error);
      } finally {
        // 接口调用失败后，页面状态回滚
        if (!succeeded) {
          this.comment.Liked = !isLike;
          this.comment.LikeCount -= diff;
        }
      }
    },
    /**
     * 给回复发回复
     */
    onReplyToReply(data) {
      this.replyList.unshift(data);
      this.totalReplyCount += 1;
      this.comment.ReplyCount = this.totalReplyCount;
      this.$emit('reply');
    },
    /**
     * 给评论发回复
     */
    async onReplyToComment(draftInfo) {
      const loginUser = this.loginUserInfo();
      const { content, fileList, onSucceed } = draftInfo;
      const newCommentReply = {
        // 接口必传字段：
        CommentID: this.comment.ID,
        Content: content,
        UserAvatar: loginUser.avatar,
        // 回复给
        // RespondedUserID: this.comment.UserID,
        // RespondedUserName: this.comment.UserName,
        Attachments: fileList.map(n => n.result),
        Url: this.notificationLink(),
        UrlParams: JSON.stringify({
          fpath: encodeURIComponent(this.pageUrl),
          showComment: true,
          commentId: this.comment.ID,
        }),
      }

      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl('CMTS-SaveReply'),
          data: { reply: JSON.stringify(newCommentReply)}
        });
        console.log('onReplyToComment', data)

        if (data.state !== '1' || !data.data) {
          this.$TM.Toast('发送失败');
          return;
        }
        this.$TM.Toast('发送成功');

        // TODO 接口没有返回新数据对象，暂时用前端数据
        Object.assign(newCommentReply, {
          ID: 'reply-reply-m-' + parseInt(Math.random() * 1000), // TODO UI-mock
          ReplyTime: Date().toLocaleString(), // TODO UI-mock
          UserID: loginUser.userId, // TODO UI-mock
          UserName: loginUser.userName, // TODO UI-mock
          Liked: false, // TODO UI-mock
          LikeCount: 0, // TODO UI-mock
          IsFavorite: false, // TODO UI-mock
          FavoriteCount: 0, // TODO UI-mock
          IsDeleted: false, // TODO UI-mock
          CanCancel: true, // TODO UI-mock
        });

        // 调接口成功后清空输入框内容
        onSucceed();
        // 评论完将新添加评论展示出来
        this.replyList.unshift((data && data.data) || newCommentReply);
        this.totalReplyCount += 1;
        this.comment.ReplyCount = this.totalReplyCount;
        this.$emit('reply');
      } catch (error) {
        console.log(error);
        this.$TM.Toast('发送失败');
      }
    },
    onDeleteReply(replyItem) {
      this.totalReplyCount -= 1;
      this.$emit('delete-reply', replyItem);
    },
    onScroll(e) {
      const { deltaY } = e;
      console.log('onScroll', e);
      if (deltaY < 0 || !this.replyList || !this.replyList.length || this.isLoading || this.hasLoadedAll) {
        // 滚向顶部时、没有数据时、正在加载数据时、及全部加载完时，滚动无需处理
        return;
      }
      requestAnimationFrame(this.scrollHanlder);
    },
    scrollHanlder() {
      const container = this.$refs.scrollAreaRef;
      const lastItem = container.querySelector('.reply-item-in-comment-detail:last-child');
      // 提前 200px 获取下一页数据
      scrollPaging(container, lastItem, 200, this.fetchPageData)
    },
    confirmClose() {
      this.reset();
      this.popupVisible = false;
    },
    /**
     * van-popup只有close和closed事件，并且不支持取消关闭
     * 因此需要自己实现关闭图标/按钮，在点击时自己才能控制关闭与否
     */
    onBeforeCloseReplyDialog() {
      if (checkDirty(this.$refs.dialogContentRef)) {
        this.$TM.Dialog.confirm({
          title: '确认',
          message: '如果选择确定关闭，已编辑的内容都将被丢弃。',
          messageAlign: 'left',
          showCancelButton: true,
          closeOnClickOverlay: true,
          confirmButtonColor: 'var(--theme-color)',
        })
          .then(_ => {
            this.confirmClose();
          })
          .catch(_ => {
            // this.popupVisible = true;
          });
      } else {
        this.confirmClose();
      }
    },
    onClickContent(e) {
      const el = e.target;
      // 如果点击了图片，则放大查看
      if (el.nodeName === 'IMG' && el.currentSrc) {
        this.$TM.ImagePreview({images:[el.currentSrc]});
      } else if(el.dataset.userId) {
        // 点击了提及人，则弹出人员信息
        this.mentionedUserId = el.dataset.userId;
        this.personnelInfoVisible = true;
      } else {
        // 点击了内容，则直接弹出回复框
        this.$refs.draftEditorRef.showInputPopup();
      }
    },
    handleDownloadFile(attachment) {
      const { AttachmentUrl, FileName } = attachment;
      saveAs({
        url: AttachmentUrl,
        fileName: FileName,
      });
    },
  },
}
</script>

<style></style>
